import type { FC, ReactNode } from 'react'

import { useRef } from 'react'
import { gsap, useGSAP } from '@/lib/gsap'
import { prefersReducedMotion } from '@/lib/motion'
import { cn } from '@/lib/utils'
import Img from '@/components/generals/img'

type MaskShape = 'pill' | 'circle' | 'slit'

interface MaskRevealProps {
    id: string
    words: [string, string]
    caption: string
    image: string
    alt: string
    shape?: MaskShape
    label?: string
    className?: string
    /** Tailwind object-position class that keeps the subject in frame, e.g. 'object-[72%_50%]'. */
    focus?: string
}

const SHAPES: Record<MaskShape, { from: string, to: string }> = {
    pill: { from: 'inset(38% 41% 38% 41% round 999px)', to: 'inset(0% 0% 0% 0% round 0px)' },
    circle: { from: 'circle(7% at 50% 50%)', to: 'circle(75% at 50% 50%)' },
    slit: { from: 'inset(0% 48.5% 0% 48.5%)', to: 'inset(0% 0% 0% 0%)' }
}

/**
 * A pinned chapter opener: the photograph grows out of a small window while the two
 * halves of the title drift apart, then the caption settles in.
 */
const MaskReveal: FC<MaskRevealProps> = ({
    id,
    words,
    caption,
    image,
    alt,
    shape = 'pill',
    label,
    className,
    focus
}): ReactNode => {
    const root = useRef<HTMLElement>(null)

    useGSAP(() => {
        const element = root.current

        if (!element) return

        const frame = element.querySelector('[data-mask-frame]')
        const photo = element.querySelector('[data-mask-photo]')
        const first = element.querySelector('[data-word="a"]')
        const second = element.querySelector('[data-word="b"]')
        const note = element.querySelector('[data-mask-caption]')

        if (prefersReducedMotion()) {
            gsap.set(frame, { clipPath: SHAPES[shape].to })

            return
        }

        gsap.set(frame, { clipPath: SHAPES[shape].from })
        gsap.set(note, { autoAlpha: 0, y: 24 })

        gsap.timeline({
            scrollTrigger: {
                trigger: element,
                start: 'top top',
                end: 'bottom bottom',
                scrub: 0.8
            }
        })
            .to(frame, { clipPath: SHAPES[shape].to, ease: 'power2.inOut', duration: 1 }, 0)
            .fromTo(photo, { scale: 1.35 }, { scale: 1, ease: 'power2.out', duration: 1 }, 0)
            .to(first, { xPercent: -28, yPercent: -18, ease: 'power2.inOut', duration: 1 }, 0)
            .to(second, { xPercent: 28, yPercent: 18, ease: 'power2.inOut', duration: 1 }, 0)
            .to(note, { autoAlpha: 1, y: 0, duration: 0.3 }, 0.72)
    }, { scope: root, dependencies: [shape] })

    return (
        <section id={id} ref={root} aria-label={`${words[0]} ${words[1]}`} className={cn('relative h-[260vh] bg-ink', className)}>
            <div className='sticky top-0 flex h-[100svh] items-center justify-center overflow-hidden'>
                <div data-mask-frame className='absolute inset-0 overflow-hidden'>
                    <div data-mask-photo className='absolute inset-0'>
                        <Img src={image} alt={alt} sizes='100vw' className={focus} />
                    </div>

                    <div aria-hidden='true' className='absolute inset-0 bg-ink/25' />
                </div>

                {label && (
                    <p className='absolute left-0 top-[calc(var(--header-height)+24px)] label-mono text-bone gutter'>
                        {label}
                    </p>
                )}

                <h2 className='relative z-10 flex select-none flex-col items-center text-center font-heading text-[clamp(76px,15vw,250px)] leading-[0.8] text-bone mix-blend-difference'>
                    <span data-word='a' className='block will-change-transform'>
                        {words[0]}
                    </span>

                    <span data-word='b' className='block will-change-transform'>
                        {words[1]}
                    </span>
                </h2>

                <p data-mask-caption className='absolute inset-x-0 bottom-10 text-center label-mono text-bone gutter'>
                    {caption}
                </p>
            </div>
        </section>
    )
}

export default MaskReveal
